/* eslint-disable react-hooks/rules-of-hooks */
import { FC, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { NotificationManager } from "../../services/api/NotificationManager";
import {
  GetNotificationSettingsResponse,
  UpdateNotificationSettingsRequest,
} from "../../shared/payloads/notificationSettingsPayloads";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { Language } from "../../shared/types/websiteTypes";
import { BorderType } from "../../components/NotificationEditComponents/BorderSelect";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { v4 } from "uuid";
import { Branding } from "../../shared/types/commonTypes";
import Tabs from "../../components/Tabs";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import { NotificationSettingsTab } from "../../components/notification/tabs/NotificationSettingsTab";
import { NotificationRulesTab } from "../../components/notification/tabs/NotificationRulesTab";

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  createdNotificationId: string;
  language: Language;
  openedFor?: "visitorSummary" | "registrationSummary" | "orderSummary";
  data?: GetNotificationSettingsResponse;
  branding: Branding;
}

type TabType = "rules" | "pairing" | "design" | "settings";

const notificationStyleId = v4();

const ShoptetCouponCodeNotificationModal: FC<SummaryModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  createdNotificationId,
  language,
  data,
  branding,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>("rules");
  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );
  const [couponCode, setCouponCode] = useState<string>(
    data?.discountCouponName || "",
  );
  const [couponSale, setCouponSale] = useState<string>(
    data?.discountCouponValue?.toString() || "",
  );
  const [selectedSaleType, setSelectedSaleType] = useState<string>(
    data?.discountCouponType || "",
  );
  const [couponSaleType, setCouponSaleType] = useState<string>(
    data?.discountCouponAppliableTo || "",
  );
  const [notificationHtml, setNotificationHtml] = useState<string>("");

  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.backgroundColor || null,
  );
  const [textColor, setTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.textColor || null,
  );
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconBackgroundColor || null,
  );
  const [iconColor, setIconColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconColor || null,
  );
  const [borderRadius, setBorderRadius] = useState<BorderType | null>(
    data?.notificationVisualSettings?.roundness || null,
  );
  const [icon, setIcon] = useState<string | null>(
    data?.notificationVisualSettings?.icon || null,
  );

  const [backgroundOpacity, setBackgroundOpracity] = useState<number | null>(
    null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(true);
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(null);

  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  useEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const couponCodeDefault = data?.discountCouponName || "";

    const selectedSaleTypeDefault = data?.discountCouponType || "";

    const couponSaleTypeDefault = data?.discountCouponAppliableTo || "";

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const defaultCouponSale = data?.discountCouponValue?.toString() || "";

    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const activeDefault =
      data?.notificationVisualSettings?.activeSize ?? branding?.activeSize;

    const iconColorDefault =
      data?.notificationVisualSettings?.iconColor ??
      branding?.iconColor ??
      NotificationDefaultSettings.IconColor;

    const showIconDefault =
      data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true;

    const defaultIconBackgroundColor =
      data?.notificationVisualSettings?.iconBackgroundColor ??
      branding?.iconBackgroundColor ??
      iconBackgroundColor ??
      NotificationDefaultSettings.IconBackgroundColor;

    if (
      borderRadius !== null &&
      (data?.notificationVisualSettings?.roundness !== null &&
      data?.notificationVisualSettings?.roundness !== undefined
        ? borderRadius !== data?.notificationVisualSettings?.roundness
        : borderRadius !== branding?.borderRadius)
    ) {
      setDetectedChange(true);
    } else if (
      backgroundOpacity !== null &&
      (data?.notificationVisualSettings?.backgroundOpacity !== null &&
      data?.notificationVisualSettings?.backgroundOpacity !== undefined
        ? backgroundOpacity !==
          data?.notificationVisualSettings?.backgroundOpacity
        : backgroundOpacity !== branding?.backgroundOpacity)
    ) {
      setDetectedChange(true);
    } else if (backgroundColor && backgroundColor !== defaultBackgroundColor) {
      setDetectedChange(true);
    } else if (textColor && textColor !== defaultTextColor) {
      setDetectedChange(true);
    } else if (activeDefault && activeDefault !== active) {
      setDetectedChange(true);
    } else if (showIcon !== showIconDefault) {
      setDetectedChange(true);
    } else if (
      iconBackgroundColor &&
      iconBackgroundColor !== defaultIconBackgroundColor
    ) {
      setDetectedChange(true);
    } else if (
      iconBackgroundRadius &&
      (data?.notificationVisualSettings?.iconBackgroundRadius !== null &&
      data?.notificationVisualSettings?.iconBackgroundRadius !== undefined
        ? iconBackgroundRadius !==
          data?.notificationVisualSettings?.iconBackgroundRadius
        : iconBackgroundRadius !== branding?.iconBackgroundRadius)
    ) {
      setDetectedChange(true);
    } else if (iconColor && iconColor !== iconColorDefault) {
      setDetectedChange(true);
    } else if (
      icon &&
      (data?.notificationVisualSettings?.icon
        ? icon !== data?.notificationVisualSettings?.icon
        : icon !== NotificationDefaultSettings.CouponIcon)
    ) {
      setDetectedChange(true);
    } else if (withRepeat !== withRepeatDefault) {
      setDetectedChange(true);
    } else if (couponCodeDefault !== couponCode) {
      setDetectedChange(true);
    } else if (defaultCouponSale !== couponSale) {
      setDetectedChange(true);
    } else if (selectedSaleType !== selectedSaleTypeDefault) {
      setDetectedChange(true);
    } else if (couponSaleTypeDefault !== couponSaleType) {
      setDetectedChange(true);
    } else {
      setDetectedChange(false);
    }
  }, [
    borderRadius,
    selectedSaleType,
    couponSaleType,
    active,
    withRepeat,
    couponCode,
    icon,
    couponSale,
    textColor,
    iconColor,
    backgroundColor,
    backgroundOpacity,
    showIcon,
    iconBackgroundColor,
    iconBackgroundRadius,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.roundness,
    branding?.brandingIsShowingIcon,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconColor,
    branding?.activeSize,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
  ]);

  const onCloseAndClearInputs = () => {
    setSelectedTab("rules");
    setWithRepeat(true);
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const onSave = async () => {
    try {
      const _notificationManager = NotificationManager.getManager();
      const body: UpdateNotificationSettingsRequest = {
        id: createdNotificationId,
        isActive: true,
        repeatNotification: withRepeat,
        discountCouponName: couponCode,
        discountCouponValue: Number(couponSale),
        discountCouponType: selectedSaleType,
        discountCouponAppliableTo: couponSaleType,
        notificationVisualSettings: {
          backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
          textColor: textColor ?? branding?.textColor ?? "",
          iconBackgroundColor:
            iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
          iconColor: iconColor ?? branding?.iconColor ?? "",
          icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
          roundness: borderRadius ?? branding?.borderRadius ?? 0,
          linkTextColor: "",
          linkButtonBackgroundColor: "",
          backgroundOpacity:
            backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
          activeSize: active ?? branding?.activeSize ?? "classic",
          isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
          iconBackgroundRadius:
            iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
        },
      };

      await _notificationManager.updateNotificationSettingsAsync(body);
      onSaveSuccess?.();
      onCloseAndClearInputs();
    } catch (e: any) {
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  useEffect(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setBackgroundOpracity(
      data?.notificationVisualSettings?.backgroundOpacity ??
        branding?.backgroundOpacity ??
        null,
    );
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : true,
    );
    setIcon(data?.notificationVisualSettings?.icon ?? null);
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
  }, [
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconBackgroundColor,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.activeSize,
    branding?.iconBackgroundRadius,
  ]);

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: branding?.textColor,
              backgroundColor: branding?.backgroundColor,
              borderRadius: branding?.borderRadius,
              backgroundOpacity: branding?.backgroundOpacity,
              iconColor: branding?.iconColor,
              iconBackgroundColor: branding?.iconBackgroundColor,
              iconBackgroundRadius: branding?.iconBackgroundRadius,
              activeSize: branding?.activeSize,
              brandingIsShowingIcon: branding?.brandingIsShowingIcon,
              logoDisplay: branding?.logoDisplay,
              logo: branding?.logo,
              logoBackgroundColor: branding?.logoBackgroundColor,
              logoText: branding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: "shoptetCouponCode",
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor:
                iconColor ??
                data?.notificationVisualSettings?.iconColor ??
                branding?.iconColor ??
                "",
              icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
            },
          },
        },
        "shoptetCouponCode",
        undefined,
        undefined,
        undefined,
        language,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        couponCode,
        Number(couponSale),
        selectedSaleType,
        couponSaleType,
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    active,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
    branding?.activeSize,
    branding?.backgroundColor,
    branding?.backgroundOpacity,
    branding?.borderRadius,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
    branding?.iconColor,
    branding?.textColor,
    couponCode,
    couponSale,
    couponSaleType,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.textColor,
    icon,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    language,
    selectedSaleType,
    showIcon,
    textColor,
  ]);

  const onReset = () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpracity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setIconColor(null);
    setIcon(null);
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
    setCouponCode(data?.discountCouponName || "");
    setCouponSale(data?.discountCouponValue?.toString() || "");
    setSelectedSaleType(data?.discountCouponType || "");
    setCouponSaleType(data?.discountCouponAppliableTo || "");
  };

  const tabs = [
    {
      value: "settings",
      label: t("campaign.notifications.settings.customization.title"),
      content: (
        <NotificationSettingsTab
          notificationType='shoptetCouponCode'
          couponCode={couponCode}
          couponSale={couponSale}
          couponSaleType={couponSaleType}
          selectedSaleType={selectedSaleType}
          setCouponCode={setCouponCode}
          setCouponSale={setCouponSale}
          setCouponSaleType={setCouponSaleType}
          setSelectedSaleType={setSelectedSaleType}
        />
      ),
    },
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: (
        <NotificationRulesTab
          rulesType='shoptetCouponCode'
          withRepeat={withRepeat}
          onToggleWithRepeat={() => setWithRepeat(!withRepeat)}
          t={t}
        />
      ),
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          data={data}
          defaultIcon={NotificationDefaultSettings.CouponIcon}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpracity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon={false}
          icon={icon}
          iconColor={iconColor}
          setIcon={setIcon}
          setIconColor={setIconColor}
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      title={t("campaign.notifications.shoptetCouponCode.title")}
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
    >
      <Tabs
        onTabChange={handleTabChange}
        selectedTab={selectedTab}
        tabs={tabs}
      />
    </NotificationModalBase>
  );
};

export default ShoptetCouponCodeNotificationModal;
